import React from 'react';
import { Box, Drawer, IconButton, Link, Toolbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OrderPatternOrder } from '../../api/services';
import { formatCents, formatDate } from '../../utils/format';

// The day drawer (sc-6127): click a day column header in the combined table
// to see every order placed that day — full line list with values, method,
// expedited badge, requested-vs-delivered dates, origin, digest linkage,
// billed freight, and the HQ order link.

const Kv: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Typography variant="body2" sx={{ my: 0.25 }}>
    <Box component="span" sx={{ display: 'inline-block', width: 132, color: 'text.secondary' }}>{label}</Box>
    {children}
  </Typography>
);

const OrderBlock: React.FC<{ order: OrderPatternOrder }> = ({ order }) => {
  // The Qty column is HQ's box grain (line.quantity) — the Total must sum the
  // SAME grain or a boxed order reads as a data error; units ride the title
  // (the DigestOutcomes box-primary/units-in-tooltip convention).
  const totalQty = order.lines.reduce((sum, line) => sum + line.quantity, 0);
  const totalUnits = order.lines.reduce((sum, line) => sum + line.units, 0);
  return (
  <Box sx={{ mt: 2.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Order {formatDate(order.date)}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      HQ order{' '}
      {order.hq_url
        ? <Link href={order.hq_url} target="_blank" rel="noopener noreferrer">{`#${order.id}`}</Link>
        : <span>{`#${order.id}`}</span>}
      {order.quote_id != null && <span>{` · quote #${order.quote_id}`}</span>}
    </Typography>
    <Kv label="Shipped by">
      {order.method ?? '—'}
      {order.expedited && (
        <Box component="span" sx={{ color: '#a05a00', fontWeight: 700 }}> · expedited</Box>
      )}
    </Kv>
    <Kv label="Placed">{formatDate(order.date)}</Kv>
    <Kv label="Requested delivery">{formatDate(order.desired_date)}</Kv>
    <Kv label="Delivered">{formatDate(order.delivered_date)}</Kv>
    <Kv label="Origin">{order.origin ?? '—'}</Kv>
    <Kv label="From digest">{order.from_digest ? 'yes — placed from the restock digest' : 'no'}</Kv>
    <Kv label="Freight billed">{order.freight_cents ? formatCents(order.freight_cents) : '$0'}</Kv>
    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', marginTop: 8 }}>
      <thead>
        <tr>
          <th style={cellStyle('left')}>Item</th>
          <th style={cellStyle('right')}>Qty</th>
          <th style={cellStyle('right')}>Value</th>
        </tr>
      </thead>
      <tbody>
        {order.lines.map((line, i) => (
          <tr key={`${line.sku}-${i}`}>
            <td style={cellStyle('left')}>
              {line.name ?? line.sku}
              <br />
              <span style={{ color: '#999', fontSize: 11 }}>{line.sku}</span>
            </td>
            <td style={cellStyle('right')} title={`${line.units} unit${line.units === 1 ? '' : 's'}`}>
              {line.quantity}
            </td>
            <td style={cellStyle('right')}>{formatCents(line.value_cents)}</td>
          </tr>
        ))}
        <tr>
          <td style={cellStyle('left')}><b>Total</b></td>
          <td style={cellStyle('right')} title={`${totalUnits} unit${totalUnits === 1 ? '' : 's'}`}>
            <b>{totalQty}</b>
          </td>
          <td style={cellStyle('right')}><b>{formatCents(order.value_cents)}</b></td>
        </tr>
      </tbody>
    </table>
  </Box>
  );
};

const cellStyle = (align: 'left' | 'right'): React.CSSProperties => ({
  padding: '3px 6px',
  borderBottom: '1px solid #eee',
  textAlign: align,
});

export const OrderDayDrawer: React.FC<{
  day: string | null;
  orders: OrderPatternOrder[];
  onClose: () => void;
}> = ({ day, orders, onClose }) => (
  <Drawer anchor="right" open={!!day} onClose={onClose}>
    <Toolbar /> {/* Spacer — the app bar sits above drawers (zIndex.drawer + 1) */}
    <Box sx={{ width: 400, p: 2.5, position: 'relative' }}>
      <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', top: 10, right: 10 }} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography variant="h6">{day ? formatDate(day) : ''}</Typography>
      <Typography variant="body2" color="text.secondary">
        {orders.length} order{orders.length === 1 ? '' : 's'} placed this day
      </Typography>
      {orders.map((o) => <OrderBlock key={o.id} order={o} />)}
    </Box>
  </Drawer>
);
